import React, {
  useEffect,
  useReducer,
  useState,
  useMemo,
  useCallback,
  useRef,
} from "react";
import { useHistory } from "react-router-dom";

import { Card, Box, CircularProgress, LinearProgress } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";

import { ListToolbar, Title, useNotify, useRedirect } from "react-admin";

import { ForceTitle } from "../../../components/common/ForceTitle";

import { useTrackDataFilters } from "../../../utils/useTrackDataFilters";
import { useTrackView } from "./hooks/useTrackView";
import { useTrackManagement } from "../../../utils/useTrackManagement";
import { useEquipmentTypes } from "./hooks/useEquipmentTypes";
import { useTrackActions } from "./hooks/useTrackActions";
import { useTrackPlacementUpdate } from "./hooks/useTrackPlacementUpdate";
import { TrackProvider } from "./TrackContext";
import { useTrackDragDrop } from "./useTrackDragDrop";
import { trackReducer, createTrackActions } from "../../../utils/trackReducer";
import { colorReducer, createColorActions } from "./reducers/colorReducer";
import { ErrorBoundary } from "../../../components/common/ErrorBoundary";
import { setAccountColorMap } from "../../../utils/trackListUtils";

import { TrackLegend } from "../../../components/tracks/TrackLegend";

import { getBackPath, deepRedirect } from "../../../utils/utils";

import { UNSPOTTED_TRACK_ID, UserType } from "../../../utils/constants";
import { isOutbound } from "../../../utils/trackCarPersistence";
import { logError } from "../../../utils/appUtils";
import { useUser } from "../../../authenticatorApp/UserContext";

import { TrackGridView } from "./components/TrackGridView";
import { TrackListView } from "./components/TrackListView";
import { TrackFilters } from "./components/TrackFilters";
import { ListActions } from "./components/ListActions";

/**
 * PRIVACY MODEL (Customer Portal):
 *
 * Customers see the live track layout (same index + subscriptions as the
 * manager view), minus the Unspotted lane, read-only.
 * Customers see all railcars on all tracks for accurate spatial/occupancy awareness.
 * Sensitive details (account, product, status, PO#, etc.) are masked in the UI for
 * cars belonging to other accounts.
 *
 * NOTE: This is "casual privacy" masking, NOT a security boundary. Full shipment
 * data is transmitted to the client. If stricter data isolation is required,
 * implement field-level redaction in the backend API.
 *
 * @see isRestrictedForCustomer in trackListUtils.js for centralized restriction logic
 */

const useStyles = makeStyles((theme) => ({
  list: {
    marginRight: 0,
    width: "100%",
    overflow: "hidden",
  },
  filtersWrapper: {
    padding: theme.spacing(2),
    paddingBottom: 0,
  },
  locationTitle: {
    flexShrink: 0,
  },
  trackContainer: {
    padding: (props) => theme.spacing(props.compactMode ? 1 : 2),
  },
  trackHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginTop: (props) => theme.spacing(props.compactMode ? 0.5 : 2),
    marginLeft: theme.spacing(1),
    marginRight: theme.spacing(1),
    gap: theme.spacing(1),
  },
  track: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "flex-start",
    padding: (props) => theme.spacing(props.compactMode ? 0.25 : 1),
    minHeight: theme.spacing(12),
    maxHeight: "65vh",
    overflowY: "auto",
    overflowX: "hidden",
    minWidth: theme.spacing(25),
    border: "1px dashed",
    borderRadius: theme.spacing(1),
    borderColor:
      theme.palette?.divider || theme.palette?.grey?.[400] || "#bdbdbd",
    backgroundColor: theme.palette.background?.paper || "#fff",
    transition: "all 0.2s ease",
    "&::-webkit-scrollbar": {
      width: "5px",
    },
    "&::-webkit-scrollbar-track": {
      background: "transparent",
    },
    "&::-webkit-scrollbar-thumb": {
      background: theme.palette.divider,
      borderRadius: "10px",
    },
  },
  listViewContainer: {
    "& .MuiTableRow-root": {
      transition: "all 0.2s ease",
      "&:hover": {
        backgroundColor:
          theme.palette.type === "dark"
            ? "rgba(255, 255, 255, 0.05)"
            : "rgba(0, 0, 0, 0.04)",
        transform: "translateX(2px)",
        boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
      },
      "&:not(:last-child)": {
        borderBottom: `1px solid ${
          theme.palette?.divider || theme.palette?.grey?.[300] || "#e0e0e0"
        }`,
      },
    },
    "& .MuiTableCell-root": {
      padding: (props) =>
        props.compactMode ? theme.spacing(0.5, 0.75) : theme.spacing(1.5, 2),
      fontSize: (props) => (props.compactMode ? "0.75rem" : "0.875rem"),
      lineHeight: (props) => (props.compactMode ? "1.3" : "1.5"),
      borderBottom: "none",
    },
    "& .MuiTableHead-root .MuiTableCell-root": {
      padding: (props) =>
        props.compactMode ? theme.spacing(0.5, 0.75) : theme.spacing(1.5, 2),
      fontSize: (props) => (props.compactMode ? "0.6875rem" : "0.8125rem"),
      fontWeight: 600,
      lineHeight: (props) => (props.compactMode ? "1.2" : "1.4"),
      borderBottom: `2px solid ${
        theme.palette?.divider || theme.palette?.grey?.[400] || "#bdbdbd"
      }`,
    },
    "& .MuiTableBody-root .MuiTableRow-root": {
      height: (props) => (props.compactMode ? "auto" : "auto"),
      minHeight: (props) => (props.compactMode ? "36px" : "48px"),
    },
  },
  loadingShell: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    minHeight: "40vh",
    gap: theme.spacing(1.5),
  },
}));

// TrackFilters component extracted to components/TrackFilters.js
// ListActions component extracted to components/ListActions.js
// Data fetching and subscription logic unified in utils/useTrackManagement.js

/**
 * Main TrackManagementAdmin component for managing railcar placement on tracks (Admin view)
 *
 * Features:
 * - Drag-and-drop railcar placement
 * - Real-time updates via GraphQL subscriptions
 * - Grid and list view modes
 * - Account and product filtering
 * - Track capacity monitoring
 * - Equipment type management
 * - Color coding for accounts and products
 *
 * Architecture:
 * - Uses reducers for state management (trackReducer, colorReducer)
 * - Custom hooks for business logic (data, subscriptions, filters, actions)
 * - Context provider for shared state (TrackContext)
 * - Component extraction for UI separation
 *
 * @param {Object} props - Component props
 * @param {Object} props.location - React Router location object
 * @param {Object} props.location.state - Router state
 * @param {string} props.location.state.railCarNumber - Optional initial search term
 * @returns {JSX.Element} TrackManagementAdmin component
 */
export const TrackManagementAdmin = (props) => {
  const { appUser } = useUser();
  const notify = useNotify();
  const redirect = useRedirect();
  const history = useHistory();

  const isDraggingRef = useRef(false);
  const handleDragEndFlushRef = useRef(null);
  const readonly = props.readonly;

  // ============================================================================
  // STATE MANAGEMENT
  // ============================================================================

  // Use reducers for related state groups
  const [trackState, trackDispatch] = useReducer(trackReducer, {
    loading: true,
    tracks: [],
    processedCarsMap: {},
    staleTracks: {},
  });

  const [colorState, colorDispatch] = useReducer(colorReducer, {
    colorsLoaded: 0,
    loadedAccountIds: new Set(),
  });

  // Create stable action functions using factory pattern (consistent with TrackLocation)
  // CRITICAL: Memoize to prevent new function references on every render (prevents infinite loops)
  // dispatch from useReducer is stable, so we can safely memoize based on dispatch
  const trackActions = useMemo(
    () => createTrackActions(trackDispatch),
    [trackDispatch],
  );
  const colorActions = useMemo(
    () => createColorActions(colorDispatch),
    [colorDispatch],
  );

  // Destructure actions for easier access
  const { setLoading, setTracks, setProcessedCarsMap, setStaleTracks } =
    trackActions;

  const { setColorsLoaded, setLoadedAccountIds } = colorActions;

  const { loading, tracks, processedCarsMap, staleTracks } = trackState;
  const { colorsLoaded, loadedAccountIds } = colorState;

  // Selection state
  const [selectedShipmentIds, setSelectedShipmentIds] = useState([]);

  /**
   * Stable NOOP function to prevent unnecessary re-renders when passing callbacks
   */
  const stableNoop = useCallback(() => {}, []);

  /**
   * Navigate to shipment detail page
   */
  const openShipment = useCallback(
    (shipment) => {
      if (!shipment || !shipment.id) {
        console.error("openShipment called with invalid item:", shipment);
        return;
      }
      if (isOutbound(shipment)) {
        const toPath = `/outboundShipments/${shipment.id}`;
        deepRedirect(history, toPath, getBackPath(props.location), {});
      } else {
        const toPath = `/inboundShipments/${shipment.id}`;
        deepRedirect(history, toPath, getBackPath(props.location), {});
      }
    },
    [history, props.location],
  );

  // ============================================================================
  // LOCAL STATE
  // ============================================================================

  // Equipment type map (metadata - can stay as useState for now)
  const [equipmentTypeMap, setEquipmentTypeMap] = useState({});

  // ============================================================================
  // CUSTOM HOOKS
  // ============================================================================

  // Manage view state
  const {
    viewMode,
    setViewMode,
    compactMode,
    setCompactMode,
    gridSize,
    setGridSize,
  } = useTrackView({
    initialViewMode: "grid",
    initialCompactMode: false,
    initialGridSize: 4,
  });

  const classes = useStyles({ compactMode });

  // Add backdrop style to classes if not present
  // Note: classes are generated from useStyles which is defined outside
  const { location, basePath } = props;
  const backPath = useMemo(
    () => getBackPath({ location, basePath }),
    [location, basePath],
  );
  const reloadYardId = window.appUser?.reloadYardId;
  const isCustomer =
    window.appUser?.type === UserType.CUSTOMER ||
    appUser?.type === UserType.CUSTOMER;
  const customerAccountId = isCustomer
    ? appUser?.accountId || appUser?.account?.id || window.appUser?.accountId
    : null;

  // For customer portal: show all spotted cars, mask details in the UI layer.
  // The Unspotted lane is yard-internal staging, so customers never see it.
  const processedCarsMapFiltered = useMemo(() => {
    if (!isCustomer) return processedCarsMap;
    if (!customerAccountId) return {};
    const { [UNSPOTTED_TRACK_ID]: _unspotted, ...spotted } =
      processedCarsMap || {};
    return spotted;
  }, [isCustomer, customerAccountId, processedCarsMap]);

  // Manage filters (account, product, search term)
  const {
    searchTerm,
    setSearchTerm,
    accountFilter,
    setAccountFilter,
    productFilter,
    setProductFilter,
    trackFilter,
    setTrackFilter,
    visibleCarsMap,
    visibleCars,
    foundCarMap,
  } = useTrackDataFilters({
    mode: "manager",
    processedCarsMap: processedCarsMapFiltered,
    initialSearchTerm: props.location?.state?.railCarNumber
      ? props.location.state.railCarNumber.toLowerCase()
      : null,
    viewMode,
  });

  const prevTrackFilterRef = useRef(trackFilter);

  // Single blocking loader until the first processedCarsMap payload exists (tracks may already be set).
  const hasRailcarsMapStarted = useMemo(
    () => Object.keys(processedCarsMap || {}).length > 0,
    [processedCarsMap],
  );
  const showBlockingRailcarsLoad = loading && !hasRailcarsMapStarted;


  /**
   * Filter tracks based on selected track filter
   * This ensures the Grid view only shows selected tracks, matching List view behavior
   */
  const tracksForView = useMemo(() => {
    if (!isCustomer) return tracks;
    if (!customerAccountId) return [];

    return tracks.filter((track) => {
      const trackLocationId = track.locations?.items?.[0]?.id;
      if (!trackLocationId) return false;
      const cars = processedCarsMapFiltered?.[trackLocationId] || [];
      return cars.length > 0;
    });
  }, [tracks, processedCarsMapFiltered, isCustomer, customerAccountId]);

  const visibleTracks = useMemo(() => {
    const baseTracks = tracksForView;
    if (!trackFilter || trackFilter.length === 0) return baseTracks;
    const trackFilterSet = new Set(trackFilter);
    return baseTracks.filter((track) => {
      const trackLocationId = track.locations?.items?.[0]?.id;
      return trackLocationId && trackFilterSet.has(trackLocationId);
    });
  }, [tracksForView, trackFilter]);

  // Unified track management hook (fetching + subscriptions)
  const {
    getTracks,
    getTrackCars,
    refreshTracksDebounced: getTracksDebounced,
    justCompletedDragRef: unifiedJustCompletedDragRef,
    setProcessedCarsMapWithRefSync,
  } = useTrackManagement({
    setTracks,
    setProcessedCarsMap,
    setStaleTracks,
    setLoading,
    tracks,
    notify,
    reloadYardId,
    isCrewApp: false,
    isDraggingRef,
    handleDragEndFlushRef,
  });

  const handleAccountsLoaded = useCallback(
    (accounts = []) => {
      const colorMap = {};
      const ids = [];
      accounts.forEach((account) => {
        if (account?.id) {
          ids.push(account.id);
          if (account.color) {
            colorMap[account.id] = account.color;
          }
        }
      });
      setAccountColorMap(colorMap);
      setLoadedAccountIds(new Set(ids));
      setColorsLoaded((prev) => prev + 1);
    },
    [setColorsLoaded, setLoadedAccountIds],
  );

  // Equipment types hook (metadata for equipment types)
  useEquipmentTypes({
    setEquipmentTypeMap,
    notify,
  });

  // Actions hook (save shipment)
  const { saveShipment } = useTrackActions();

  // Flips shipment status/dates/history when a railcar is spotted onto a track
  // (dragged out of the Unspotted bucket). Matches crewApp placement behavior.
  const { updateShipmentOnPlacement } = useTrackPlacementUpdate({
    tracks,
    saveShipment,
    notify,
    logError,
  });

  // Drag & Drop hook (moved to top level context provider)
  const {
    handleDragStart: hookHandleDragStart,
    handleDragEnd: hookHandleDragEnd,
    handleDragOver,
  } = useTrackDragDrop({
    processedCarsMap,
    setProcessedCarsMap,
    setTrackData: setProcessedCarsMapWithRefSync,
    selectedShipmentIds,
    setSelectedShipmentIds,
    setLoading,
    saveShipment,
    notify,
    logError,
    redirect,
    setDragOverInfo: stableNoop, // STAFF LEVEL OPTIMIZATION: Use stable reference
    unspottedTrackId: UNSPOTTED_TRACK_ID,
    updateShipmentOnPlacement,
    refreshTracks: getTracksDebounced,
    justCompletedDragRef: unifiedJustCompletedDragRef,
    tracks,
  });

  const handleDragStart = useCallback(
    (event) => {
      isDraggingRef.current = true;
      hookHandleDragStart(event);
    },
    [hookHandleDragStart],
  );

  const handleDragEnd = useCallback(
    async (event) => {
      try {
        await hookHandleDragEnd(event);
      } finally {
        isDraggingRef.current = false;
        // onSuccess in useTrackDragDrop already set justCompletedDragRef.current = Date.now();
        // Do not overwrite with true/false or the getTrackCars stale-fetch guard breaks.
        if (handleDragEndFlushRef.current) {
          handleDragEndFlushRef.current();
        }
        setTimeout(() => {
          unifiedJustCompletedDragRef.current = 0;
        }, 2000);
      }
    },
    [hookHandleDragEnd, unifiedJustCompletedDragRef],
  );

  // ============================================================================
  // EFFECTS
  // ============================================================================

  /**
   * Initialize data on mount. getTrackCars loads yard layout then railcars
   * (layout first; per-track car queries overlap after that).
   */
  useEffect(() => {
    getTrackCars();
  }, [getTrackCars]);

  useEffect(() => {
    const prevFilter = prevTrackFilterRef.current;
    prevTrackFilterRef.current = trackFilter;

    if (trackFilter && trackFilter.length > 0) {
      getTrackCars({
        showSpinner: false,
        skipLoading: true,
        visibleTrackIds: trackFilter,
      });
      return;
    }

    if (prevFilter && prevFilter.length > 0) {
      // Reset to default fetch strategy when filter is cleared.
      getTrackCars({
        showSpinner: false,
        skipLoading: true,
        visibleTrackIds: [],
      });
    }
  }, [trackFilter, getTrackCars]);

  // STAFF LEVEL OPTIMIZATION: Stable filter setters
  const stableSetAccountFilter = useMemo(
    () => (isCustomer ? stableNoop : setAccountFilter),
    [isCustomer, setAccountFilter, stableNoop],
  );

  // ============================================================================
  // RENDER
  // ============================================================================

  const contextValue = useMemo(
    () => ({
      loading,
      setLoading,
      processedCars: visibleCars,
      processedCarsMap: processedCarsMapFiltered,
      visibleCarsMap,
      staleTracks,
      setProcessedCarsMap,
      foundCarMap,
      searchTerm,
      setSearchTerm,
      accountFilter,
      setAccountFilter: stableSetAccountFilter,
      productFilter,
      setProductFilter,
      trackFilter,
      setTrackFilter,
      backPath,
      tracks: visibleTracks,
      allTracks: tracksForView,
      getTracks,
      getTrackCars,
      getTracksDebounced,
      saveShipment,
      openShipment,
      gridSize,
      setGridSize,
      compactMode,
      setCompactMode,
      colorsLoaded,
      loadedAccountIds,
      equipmentTypeMap,
      viewMode,
      setViewMode,
      selectedShipmentIds,
      setSelectedShipmentIds,
      handleDragStart,
      handleDragOver,
      handleDragEnd,
      readonly,
      isCustomer,
      customerAccountId,
      onAccountsLoaded: handleAccountsLoaded,
    }),
    [
      loading,
      setLoading,
      visibleCars,
      processedCarsMapFiltered,
      visibleCarsMap,
      staleTracks,
      setProcessedCarsMap,
      foundCarMap,
      searchTerm,
      setSearchTerm,
      accountFilter,
      isCustomer,
      stableSetAccountFilter,
      productFilter,
      setProductFilter,
      trackFilter,
      setTrackFilter,
      backPath,
      visibleTracks,
      tracksForView,
      getTracks,
      getTrackCars,
      getTracksDebounced,
      saveShipment,
      openShipment,
      gridSize,
      setGridSize,
      compactMode,
      setCompactMode,
      colorsLoaded,
      loadedAccountIds,
      equipmentTypeMap,
      viewMode,
      setViewMode,
      selectedShipmentIds,
      setSelectedShipmentIds,
      handleDragStart,
      handleDragOver,
      handleDragEnd,
      readonly,
      customerAccountId,
      handleAccountsLoaded,
    ],
  );

  return (
    <ErrorBoundary componentName="TrackManagementAdmin">
      <TrackProvider value={contextValue}>
        <div>
          <Title title={<ForceTitle title="Track Management" />} />
          <ListToolbar {...props} actions={<ListActions />} />
          <Card className={classes.list} style={{ position: "relative" }}>
            {loading && Object.keys(processedCarsMap || {}).length > 0 && (
              <Box position="absolute" top={0} left={0} right={0} zIndex={1000}>
                <LinearProgress />
              </Box>
            )}
            <div className={classes.filtersWrapper}>
              <TrackFilters />
            </div>
            <Box px={2} pb={1}>
              <TrackLegend />
            </Box>
            <div
              id="track-management-main-area"
              data-productly="track-management-main-area"
            >
              {showBlockingRailcarsLoad ? (
                <Box className={classes.loadingShell}>
                  <CircularProgress size={28} />
                  <Box component="span">Loading railcars…</Box>
                </Box>
              ) : viewMode === "grid" ? (
                <TrackGridView />
              ) : (
                <TrackListView />
              )}
            </div>
          </Card>
        </div>
      </TrackProvider>
    </ErrorBoundary>
  );
};

// TrackListView component extracted to components/TrackListView.js
// TrackGrid component extracted to components/TrackGridView.js

